<script setup lang="tsx">
import type { PaginationState } from '@/types/page'
import { onBeforeUnmount, watch } from 'vue'

const props = withDefaults(
	defineProps<{
		totalPages?: number
		disableLimitSelect?: boolean
		tableTitle?: string
	}>(),
	{
		totalPages: 1,
	},
)

const PAGINATION_LIMITS = [25, 50, 100, 150]
const model = defineModel<PaginationState>({ default: { page: 1, limit: 50 } })

const storageKey = () => `pagination-${props.tableTitle}`

function storedLimit(): number | undefined {
	if (!props.tableTitle) return undefined

	const stored = Number(localStorage.getItem(storageKey()))
	return PAGINATION_LIMITS.includes(stored) ? stored : undefined
}

// Restored during setup so the table's initial fetch already uses the stored limit.
const restored = storedLimit()
if (restored) model.value.limit = restored

watch(
	() => model.value.limit,
	(limit) => {
		if (props.tableTitle) localStorage.setItem(storageKey(), String(limit))
	},
)

// A pager rendered behind a v-if cannot clamp its own page as it unmounts, so leaving the
// stale number behind would ask for a page the shrunken result no longer has.
onBeforeUnmount(() => {
	model.value.page = 1
})

watch(
	() => props.totalPages,
	(newTotal) => {
		if (newTotal < 1) {
			model.value.page = 1
		} else if (model.value.page > newTotal) {
			model.value.page = newTotal
		}
	},
)

function goToPage(page: number) {
	if (page >= 1 && page <= props.totalPages && page !== model.value.page) {
		model.value.page = page
	}
}
</script>

<template>
	<div class="pagination-container">
		<button data-test="first-page-button" class="pagination-icon" @click="goToPage(1)" :disabled="model.page <= 1">
			<i class="bi bi-chevron-bar-left"></i>
		</button>

		<button data-test="prev-page-button" class="pagination-icon" @click="goToPage(model.page - 1)" :disabled="model.page <= 1">
			<i class="bi bi-chevron-left"></i>
		</button>

		<span data-test="current-page">{{ model.page }} / {{ totalPages > 0 ? totalPages : 1 }}</span>

		<button data-test="next-page-button" class="pagination-icon" @click="goToPage(model.page + 1)" :disabled="model.page >= totalPages">
			<i class="bi bi-chevron-right"></i>
		</button>

		<button data-test="last-page-button" class="pagination-icon" @click="goToPage(totalPages)" :disabled="model.page >= totalPages">
			<i class="bi bi-chevron-bar-right"></i>
		</button>

		<select
			v-if="!props.disableLimitSelect"
			v-model="model.limit"
			class="ms-3 form-select form-select-sm"
			style="width: auto"
			data-test="limit-select"
		>
			<option v-for="item in PAGINATION_LIMITS" :key="item" :value="item">{{ item }}</option>
		</select>
	</div>
</template>

<style lang="sass" scoped>
.pagination-container
  align-items: center
  margin-top: 20px
  display: flex
  justify-content: center

.pagination-icon
  background: none
  border: none
  color: #007bff
  font-size: 1.4rem
  cursor: pointer
  transition: color 0.3s ease, transform 0.2s ease

.pagination-icon:hover
  color: #0056b3
  transform: scale(1.2)

.pagination-icon:disabled
  color: #ced4da
  cursor: not-allowed
</style>
